import React from 'react';
import { Link } from 'react-router-dom';
import { QrCodeIcon, Calendar, User, ArrowRight, MessageCircle, Shield, Zap } from 'lucide-react';

export default function Blog() {
  const blogPosts = [
    {
      id: 1,
      title: "The Future of Anonymous Communication: Why Privacy Matters More Than Ever",
      excerpt: "In an increasingly connected world, the need for privacy-first communication tools has never been greater. Discover how iComment.app is revolutionizing anonymous messaging.",
      author: "Alex Chen",
      date: "January 15, 2025",
      category: "Privacy",
      readTime: "5 min read",
      image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=600&h=400&fit=crop",
      featured: true
    },
    {
      id: 2,
      title: "10 Creative Ways to Use QR Codes for Anonymous Feedback",
      excerpt: "QR codes aren't just for menus anymore. Learn innovative ways businesses are using iComment.app to collect honest, anonymous feedback from customers.",
      author: "Sarah Kim",
      date: "January 12, 2025",
      category: "Business",
      readTime: "7 min read",
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&h=400&fit=crop"
    },
    {
      id: 3,
      title: "Lost and Found Revolution: How QR Codes Are Helping People Reconnect",
      excerpt: "From lost pets to misplaced wallets, see how QR codes are making it easier for good Samaritans to return lost items to their owners safely and anonymously.",
      author: "Marcus Johnson",
      date: "January 10, 2025",
      category: "Use Cases",
      readTime: "4 min read",
      image: "https://images.unsplash.com/photo-1434626881859-194d67b2b86f?w=600&h=400&fit=crop"
    },
    {
      id: 4,
      title: "Building Trust Through Transparency: Our Security Practices",
      excerpt: "Security isn't just a feature—it's our foundation. Learn about the measures we take to protect your data and maintain the anonymity that makes our service unique.",
      author: "Elena Rodriguez",
      date: "January 8, 2025",
      category: "Security",
      readTime: "6 min read",
      image: "https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=600&h=400&fit=crop"
    },
    {
      id: 5,
      title: "Small Business Success Stories: Real Results with iComment.app",
      excerpt: "Hear from real businesses that have transformed their customer communication using our anonymous messaging platform. Real stories, real results.",
      author: "Alex Chen",
      date: "January 5, 2025",
      category: "Success Stories",
      readTime: "8 min read",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=400&fit=crop"
    },
    {
      id: 6,
      title: "The Psychology of Anonymous Communication: Why It Works",
      excerpt: "Explore the psychological factors that make anonymous communication more effective for honest feedback, conflict resolution, and meaningful connections.",
      author: "Sarah Kim",
      date: "January 3, 2025",
      category: "Psychology",
      readTime: "5 min read",
      image: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=600&h=400&fit=crop"
    }
  ];

  const categories = ["All", "Privacy", "Business", "Use Cases", "Security", "Success Stories", "Psychology"];
  
  const featuredPost = blogPosts.find(post => post.featured);
  const regularPosts = blogPosts.filter(post => !post.featured);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <QrCodeIcon className="h-8 w-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">iComment.app</span>
            </Link>
            <nav className="flex items-center space-x-6">
              <Link to="/" className="text-gray-600 hover:text-gray-900">Home</Link>
              <Link to="/auth" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">Sign In</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            iComment Blog
            <span className="block text-yellow-300">Insights & Stories</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            Discover the latest insights on anonymous communication, privacy, and how QR codes are changing the way we connect.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-16">
        {/* Category Filter */}
        <div className="mb-12">
          <div className="flex flex-wrap gap-4 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                className="px-6 py-2 rounded-full bg-white text-gray-600 hover:bg-blue-50 hover:text-blue-600 transition-colors border border-gray-200"
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Featured Post */}
        {featuredPost && (
          <div className="mb-16">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="grid lg:grid-cols-2 gap-0">
                <div className="relative">
                  <img 
                    src={featuredPost.image} 
                    alt={featuredPost.title}
                    className="w-full h-64 lg:h-full object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-sm font-semibold">
                      Featured
                    </span>
                  </div>
                </div>
                <div className="p-8 flex flex-col justify-center">
                  <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
                    <div className="flex items-center space-x-1">
                      <User className="h-4 w-4" />
                      <span>{featuredPost.author}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-4 w-4" />
                      <span>{featuredPost.date}</span>
                    </div>
                    <span className="bg-gray-100 px-2 py-1 rounded text-xs">{featuredPost.category}</span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                    {featuredPost.title}
                  </h2>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {featuredPost.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">{featuredPost.readTime}</span>
                    <button className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 font-medium">
                      <span>Read More</span>
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Regular Posts Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {regularPosts.map((post) => (
            <article key={post.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <div className="relative">
                <img 
                  src={post.image} 
                  alt={post.title}
                  className="w-full h-48 object-cover"
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-white text-gray-700 px-3 py-1 rounded-full text-xs font-medium">
                    {post.category}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center space-x-4 text-xs text-gray-500 mb-3">
                  <div className="flex items-center space-x-1">
                    <User className="h-3 w-3" />
                    <span>{post.author}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Calendar className="h-3 w-3" />
                    <span>{post.date}</span>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight">
                  {post.title}
                </h3>
                <p className="text-gray-600 mb-4 leading-relaxed text-sm">
                  {post.excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">{post.readTime}</span>
                  <button className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 text-sm font-medium">
                    <span>Read More</span>
                    <ArrowRight className="h-3 w-3" />
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Newsletter Signup */}
        <div className="mt-20 bg-gradient-to-r from-blue-600 to-purple-700 rounded-xl p-8 md:p-12 text-center text-white">
          <MessageCircle className="h-16 w-16 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Stay Updated</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Get the latest insights on anonymous communication, privacy tips, and iComment.app updates delivered to your inbox.
          </p>
          <div className="max-w-md mx-auto flex gap-4">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-lg text-gray-900 border-0 focus:ring-2 focus:ring-white focus:ring-opacity-50"
            />
            <button className="bg-yellow-400 text-yellow-900 px-6 py-3 rounded-lg font-semibold hover:bg-yellow-300 transition-colors">
              Subscribe
            </button>
          </div>
          <p className="text-sm text-blue-100 mt-4">No spam, unsubscribe anytime.</p>
        </div>

        {/* Related Topics */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">Explore More Topics</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <Shield className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Privacy & Security</h3>
              <p className="text-gray-600 text-sm">Learn about data protection and anonymous communication best practices.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <Zap className="h-12 w-12 text-yellow-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Product Updates</h3>
              <p className="text-gray-600 text-sm">Stay informed about new features and improvements to iComment.app.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <MessageCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Success Stories</h3>
              <p className="text-gray-600 text-sm">Real stories from users who've transformed their communication.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}